import React, { useMemo, useState } from 'react';
import { Mic, Video, Loader, AlertCircle, CheckCircle2, X, RotateCcw, ChevronDown } from 'lucide-react';

/**
 * UploadQueuePill
 *
 * Fixed bottom-right badge that summarises in-flight streaming uploads.
 * Click to expand a popover with one row per recording + per-row retry /
 * dismiss for failures. Hidden entirely when the queue is empty.
 *
 * Props:
 *   queue: Record<recordingId, QueueEntry>
 *   onRetry(recordingId): re-attempt from last successful chunk
 *   onDismiss(recordingId): remove a failed entry from the pill
 */
const STATUS_LABEL = {
  uploading: 'Uploading',
  finalizing: 'Finalizing',
  processing: 'Processing',
  ready: 'Done',
  failed: 'Failed'
};

const UploadQueuePill = ({ queue = {}, onRetry = () => {}, onDismiss = () => {} }) => {
  const [open, setOpen] = useState(false);

  const entries = useMemo(() => {
    return Object.values(queue || {}).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [queue]);

  const counts = useMemo(() => {
    const out = { uploading: 0, finalizing: 0, processing: 0, ready: 0, failed: 0 };
    entries.forEach((e) => { if (out[e.status] !== undefined) out[e.status] += 1; });
    return out;
  }, [entries]);

  if (entries.length === 0) return null;

  const anyFailed = counts.failed > 0;
  const anyInFlight = counts.uploading + counts.finalizing + counts.processing > 0;

  const summary = anyFailed
    ? `${counts.failed} failed`
    : anyInFlight
    ? `${counts.uploading + counts.finalizing + counts.processing} uploading`
    : `${counts.ready} done`;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end">
      {open && (
        <div className="mb-2 w-80 max-h-[60vh] overflow-auto bg-white border border-gray-200 rounded-lg shadow-xl">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">Recording uploads</span>
            <div className="flex items-center gap-2">
              {entries.length > 0 && (
                <button
                  type="button"
                  onClick={() => entries.forEach((e) => onDismiss(e.recordingId))}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear all
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <ul className="divide-y divide-gray-100">
            {entries.map((e) => (
              <li key={e.recordingId} className="px-3 py-2">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 text-gray-500">
                    {e.mediaType === 'video' ? <Video size={16} /> : <Mic size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {e.itemTitle || e.itemId || 'Recording'}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      {e.status === 'failed' ? (
                        <AlertCircle size={12} className="text-red-500" />
                      ) : e.status === 'ready' ? (
                        <CheckCircle2 size={12} className="text-green-500" />
                      ) : (
                        <Loader size={12} className="animate-spin" />
                      )}
                      <span>{STATUS_LABEL[e.status] || e.status}</span>
                      {(e.status === 'uploading' || e.status === 'finalizing') && (
                        <span className="ml-1">{e.progress || 0}%</span>
                      )}
                    </div>
                    {e.status === 'uploading' || e.status === 'finalizing' ? (
                      <div className="mt-1 h-1 w-full bg-gray-200 rounded overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${e.progress || 0}%` }}
                        />
                      </div>
                    ) : null}
                    {e.errorMessage && (
                      <div className="mt-1 text-[11px] text-red-600 line-clamp-2">{e.errorMessage}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {e.status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => onRetry(e.recordingId)}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                      >
                        <RotateCcw size={12} /> Retry
                      </button>
                    )}
                    {/* Dismiss is always available — lets the user clear a
                        stale / stuck entry without waiting on the server. */}
                    <button
                      type="button"
                      onClick={() => onDismiss(e.recordingId)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                      aria-label="Dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border text-sm font-medium ${
          anyFailed
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-white border-gray-200 text-gray-700'
        }`}
        aria-label="Upload queue"
      >
        {anyFailed ? (
          <AlertCircle size={16} className="text-red-500" />
        ) : anyInFlight ? (
          <Loader size={16} className="animate-spin text-blue-500" />
        ) : (
          <CheckCircle2 size={16} className="text-green-500" />
        )}
        <span>{summary}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
    </div>
  );
};

export default UploadQueuePill;
